(() => {
  if (customElements.get('libase-product-slider')) return;
  const storageKey = 'libase-recent-products-v1';
  const validHandle = value => typeof value === 'string' && /^[\p{L}\p{N}][\p{L}\p{N}_-]{0,254}$/u.test(value);
  let memoryHistory = [];
  const history = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(value)) return [...new Set(value.filter(validHandle))].slice(0, 20);
    } catch (_) {}
    return memoryHistory;
  };
  const remember = handle => {
    if (!validHandle(handle)) return;
    memoryHistory = [handle, ...history().filter(value => value !== handle)].slice(0, 20);
    try { localStorage.setItem(storageKey, JSON.stringify(memoryHistory)); } catch (_) {}
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  customElements.define('libase-product-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try { this.config = JSON.parse(this.querySelector('[data-slider-config]').textContent); } catch (_) { return; }
      this.ready = true;
      this.events = new AbortController();
      this.track = this.querySelector('[data-slider-track]');
      this.previous = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.empty = this.querySelector('[data-slider-empty]');
      this.limit = Math.max(2, Math.min(12, Number(this.config.limit) || 8));
      this.editor = this.dataset.designMode === 'true';
      this.requests = new Map();
      this.loaded = false;
      this.loading = false;
      this.listen(this.previous, 'click', () => this.move(-1));
      this.listen(this.next, 'click', () => this.move(1));
      this.listen(this.track, 'scroll', () => this.controls());
      this.listen(this.track, 'keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.resize = new ResizeObserver(() => this.controls());
      this.resize.observe(this.track);
      if (this.dataset.mode === 'recent') {
        remember(this.dataset.currentHandle);
        this.hidden = !this.editor && this.handles().length === 0;
        this.listen(window, 'storage', event => {
          if (event.key !== storageKey && event.key !== null) return;
          this.loaded = false;
          this.hidden = !this.editor && this.handles().length === 0;
          if (this.inView) this.loadRecent();
        });
        this.listen(window, 'pageshow', event => {
          if (!event.persisted) return;
          remember(this.dataset.currentHandle);
          this.loaded = false;
          this.loadRecent();
        });
      } else {
        this.hidden = !this.editor && this.track.children.length === 0;
        this.empty.hidden = !this.editor || this.track.children.length > 0;
      }
      if ('IntersectionObserver' in window) {
        this.visibility = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.inView = true;
          if (this.dataset.mode === 'recent') this.loadRecent();
          else this.loadRatings();
        }, {rootMargin: '400px'});
        this.visibility.observe(this);
      } else {
        this.inView = true;
        if (this.dataset.mode === 'recent') this.loadRecent();
        else this.loadRatings();
      }
      this.controls();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.querySelectorAll('[data-rating-requested][hidden]').forEach(node => delete node.dataset.ratingRequested);
      this.ready = false;
    }
    listen(target, name, handler) { target?.addEventListener(name, handler, {signal: this.events.signal}); }
    handles() { return history().filter(handle => handle !== this.dataset.currentHandle).slice(0, this.limit); }
    async request(url, credentials = 'same-origin') {
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.events.signal.addEventListener('abort', abort, {once: true});
      const timer = setTimeout(abort, 8000);
      try {
        const response = await fetch(url, {credentials, signal: controller.signal});
        if (!response.ok) return null;
        return await response.json();
      } catch (_) { return null; }
      finally {
        clearTimeout(timer);
        this.events.signal.removeEventListener('abort', abort);
      }
    }
    product(handle) {
      if (!this.requests.has(handle)) {
        const root = window.Shopify?.routes?.root || '/';
        this.requests.set(handle, this.request(`${root}products/${encodeURIComponent(handle)}.js`));
      }
      return this.requests.get(handle);
    }
    async loadRecent() {
      if (this.loaded || this.loading) return;
      const generation = this.events;
      const handles = this.handles();
      this.hidden = !this.editor && handles.length === 0;
      if (!handles.length) {
        this.track.replaceChildren();
        this.empty.hidden = !this.editor;
        this.controls();
        return;
      }
      this.loading = true;
      this.track.setAttribute('aria-busy', 'true');
      const values = new Array(handles.length);
      let index = 0;
      const worker = async () => {
        while (index < handles.length) {
          if (generation.signal.aborted) return;
          const position = index++;
          values[position] = await this.product(handles[position]);
        }
      };
      await Promise.all(Array.from({length: Math.min(3, handles.length)}, worker));
      if (generation.signal.aborted) return;
      if (handles.join(',') !== this.handles().join(',')) {
        this.loading = false;
        this.loaded = false;
        this.loadRecent();
        return;
      }
      const cards = values.filter(value => value && validHandle(value.handle) && value.handle !== this.dataset.currentHandle && String(value.id) !== this.dataset.currentId).map(value => this.card(value));
      this.track.replaceChildren(...cards);
      this.track.removeAttribute('aria-busy');
      this.empty.hidden = !this.editor || cards.length > 0;
      this.hidden = !this.editor && cards.length === 0;
      this.loading = false;
      this.loaded = true;
      this.controls();
      this.loadRatings();
    }
    money(value) {
      try { return new Intl.NumberFormat(this.config.locale || 'ja', {style: 'currency', currency: this.config.currency || 'JPY'}).format(Number(value) / 100); }
      catch (_) { return String(Number(value) / 100); }
    }
    card(product) {
      const item = element('li', 'libase-item-slide');
      const link = element('a', 'libase-item-card');
      link.href = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;
      const frame = element('div', 'libase-item-card__image');
      try {
        const imageUrl = new URL(product.featured_image || '', location.origin);
        if (product.featured_image && /^https?:$/.test(imageUrl.protocol)) {
          const image = element('img');
          image.src = imageUrl.href;
          image.alt = product.title || '';
          image.width = 600;
          image.height = 600;
          image.loading = 'lazy';
          frame.append(image);
        }
      } catch (_) {}
      const price = element('div', 'libase-item-card__price');
      const amount = Number(product.price);
      if (Number.isFinite(amount) && amount > 0) {
        if (Number(product.compare_at_price) > amount) price.append(element('s', '', this.money(product.compare_at_price)));
        const text = product.price_varies ? this.config.strings.fromPrice.replace('__price__', this.money(amount)) : this.money(amount);
        price.append(element('span', '', text));
      } else price.append(element('span', '', this.config.strings.selectionPrice));
      const rating = element('div', 'libase-slider-rating');
      rating.dataset.sliderRating = '';
      rating.dataset.productHandle = product.handle;
      rating.hidden = true;
      const stars = element('span', 'libase-slider-rating__stars');
      stars.setAttribute('aria-hidden', 'true');
      const fill = element('span', '', '★★★★★');
      fill.dataset.sliderStars = '';
      stars.append(element('span', '', '★★★★★'), fill);
      const score = element('span');
      score.dataset.sliderScore = '';
      const label = element('span', 'visually-hidden');
      label.dataset.sliderRatingLabel = '';
      rating.append(stars, score, label);
      link.append(frame, element('h3', '', product.title || product.handle), price, rating);
      item.append(link);
      return item;
    }
    async loadRatings() {
      if (!this.ready || this.hidden) return;
      const generation = this.events;
      const nodes = [...this.querySelectorAll('[data-slider-rating]:not([data-rating-requested])')];
      let index = 0;
      const worker = async () => {
        while (index < nodes.length) {
          if (generation.signal.aborted) return;
          const node = nodes[index++];
          if (node.dataset.ratingRequested) continue;
          node.dataset.ratingRequested = 'true';
          const data = await this.request(`https://libase-gacha.onrender.com/review-summary?productId=${encodeURIComponent(node.dataset.productHandle)}`, 'omit');
          if (generation.signal.aborted) return;
          const average = Number(data?.avg), count = Number(data?.count);
          if (data?.ok !== true || !Number.isFinite(average) || average < 1 || average > 5 || !Number.isInteger(count) || count < 1) continue;
          node.querySelector('[data-slider-stars]').style.width = `${average * 20}%`;
          node.querySelector('[data-slider-score]').textContent = average.toFixed(1);
          node.querySelector('[data-slider-rating-label]').textContent = this.config.strings.rating.replace('__rating__', average.toFixed(1));
          node.hidden = false;
        }
      };
      await Promise.all(Array.from({length: Math.min(3, nodes.length)}, worker));
    }
    move(direction) {
      const step = this.track.clientWidth * .85;
      this.track.scrollBy({left: direction * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }
    controls() {
      if (!this.track || !this.previous || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.previous.disabled = max < 2 || this.track.scrollLeft < 2;
      this.next.disabled = max < 2 || this.track.scrollLeft >= max - 2;
    }
  });
})();
